import { useCallback, useState, type FormEvent } from 'react';
import {
  apiErrorText,
  type CeoBankAccount,
  useCreateCeoBankAccountMutation,
  useCreateCeoBankPaymentMutation,
  useGetCeoDailyPaymentsQuery,
  useListCeoBankAccountsQuery,
  useSyncCeoBankAccountsApiMutation,
  useUpdateCeoBankAccountRequisitesMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { formatMoney } from '../ceoFormat';
import { TreasuryViewNote, useTreasuryWrite } from '../treasuryAccess';
import { HeaderActions, WrapButton } from '../ui';
import { AccountCard } from './AccountCard';
import { AccountRequisitesModal } from './AccountRequisitesModal';
import { AddAccountModal } from './AddAccountModal';
import { BalanceHistorySection } from './BalanceHistorySection';
import { BankConnectionsSection } from './BankConnectionsSection';
import { DailyPaymentsSection } from './DailyPaymentsSection';
import { ManualBalanceForm } from './ManualBalanceForm';
import { StatementUploadForm } from './StatementUploadForm';
import {
  CompanyAccountsGrid,
  CompanyTitle,
  ErrorNotice,
  ErrorRow,
  HeaderRow,
  Section,
  SuccessNotice,
  SummarySubtext,
  Title,
} from './styles';
import type { BankAccountsPageProps } from './types';

/**
 * Счета по компаниям: у холдинга их несколько десятков. Ключ — ИНН: разные банки пишут название
 * одной компании по-разному, и по названию она распалась бы на несколько групп.
 */
function groupByCompany(accounts: CeoBankAccount[]): [string, CeoBankAccount[]][] {
  const groups = new Map<string, CeoBankAccount[]>();
  for (const account of accounts) {
    const key = account.entity_inn || account.entity_name;
    groups.set(key, [...(groups.get(key) ?? []), account]);
  }
  return [...groups.values()]
    .map((list): [string, CeoBankAccount[]] => [
      list.find((acc) => acc.entity_name)?.entity_name || 'Владелец не указан',
      list,
    ])
    .sort(([a], [b]) => a.localeCompare(b, 'ru'));
}

export function CeoBankAccountsView({ editable = false }: BankAccountsPageProps) {
  const can_write = useTreasuryWrite(editable);
  const {
    data: accData,
    isLoading: isLoadingAccounts,
    isError: isErrorAccounts,
    refetch: refetchAccounts,
  } = useListCeoBankAccountsQuery();
  const [syncApi, { isLoading: isSyncing }] = useSyncCeoBankAccountsApiMutation();
  const [createAccount] = useCreateCeoBankAccountMutation();
  const [createPayment] = useCreateCeoBankPaymentMutation();

  const [filterDate, setFilterDate] = useState<string>('');
  const [filterAccount, setFilterAccount] = useState<string>('');
  const [filterDirection, setFilterDirection] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');

  const {
    data: dailyData,
    isError: isErrorDaily,
    refetch: refetchDaily,
  } = useGetCeoDailyPaymentsQuery({
    date: filterDate || undefined,
    account_id: filterAccount || undefined,
    direction: filterDirection || undefined,
    category: filterCategory || undefined,
  });

  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [balanceAccount, setBalanceAccount] = useState<CeoBankAccount | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);

  // Поля нового счёта
  const [bankName, setBankName] = useState('');
  const [bik, setBik] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('settlement');
  const [currentBalance, setCurrentBalance] = useState('');
  const [purposeLabel, setPurposeLabel] = useState('');
  const [corrAccount, setCorrAccount] = useState('');
  const [entityName, setEntityName] = useState('');
  const [entityInn, setEntityInn] = useState('');
  const [entityKpp, setEntityKpp] = useState('');

  // Реквизиты плательщика заведённого счёта — отдельным запросом, остатки не трогаются
  const [updateRequisites, { isLoading: isSavingRequisites }] =
    useUpdateCeoBankAccountRequisitesMutation();
  const [requisitesAccount, setRequisitesAccount] = useState<CeoBankAccount | null>(null);
  const [reqBik, setReqBik] = useState('');
  const [reqCorr, setReqCorr] = useState('');
  const [reqName, setReqName] = useState('');
  const [reqInn, setReqInn] = useState('');
  const [reqKpp, setReqKpp] = useState('');
  const [reqType, setReqType] = useState('settlement');
  const [reqPurpose, setReqPurpose] = useState('');

  // Поля нового платежа
  const [payAccountId, setPayAccountId] = useState('');
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payDirection, setPayDirection] = useState<'inflow' | 'outflow'>('outflow');
  const [payAmount, setPayAmount] = useState('');
  const [payCounterparty, setPayCounterparty] = useState('');
  const [payPurpose, setPayPurpose] = useState('');
  const [payCategory, setPayCategory] = useState('');
  const [payDocNo, setPayDocNo] = useState('');

  const accounts = accData?.accounts ?? [];
  const summary = accData?.summary;
  const balanceHistory = accData?.balance_history ?? [];

  const refreshAfterSync = useCallback(() => {
    refetchAccounts();
    refetchDaily();
  }, [refetchAccounts, refetchDaily]);

  async function handleSync(accountId?: string) {
    try {
      setErrorNotice(null);
      const res = await syncApi(accountId ? { account_id: accountId } : undefined).unwrap();
      setSyncNotice(
        res.started_connections > 0
          ? `Запрошено обновление из банков, подключений: ${res.started_connections}. Данные появятся через несколько минут.`
          : `Снимок остатков сохранён. Консолидированный остаток: ${formatMoney(res.total_balance_after)}`,
      );
    } catch {
      setErrorNotice('Не удалось обновить счета.');
    }
  }

  function resetAccountForm() {
    setBankName('');
    setBik('');
    setAccountNumber('');
    setAccountType('settlement');
    setCurrentBalance('');
    setPurposeLabel('');
    setCorrAccount('');
    setEntityName('');
    setEntityInn('');
    setEntityKpp('');
  }

  function openRequisites(acc: CeoBankAccount) {
    setRequisitesAccount(acc);
    setReqBik(acc.bik);
    setReqCorr(acc.corr_account);
    setReqName(acc.entity_name);
    setReqInn(acc.entity_inn ?? '');
    setReqKpp(acc.entity_kpp ?? '');
    setReqType(acc.account_type);
    setReqPurpose(acc.purpose_label);
  }

  async function handleSaveRequisites(e: FormEvent) {
    e.preventDefault();
    if (!requisitesAccount) return;
    try {
      setErrorNotice(null);
      await updateRequisites({
        id: requisitesAccount.id,
        bik: reqBik.trim(),
        corr_account: reqCorr.trim(),
        entity_name: reqName.trim(),
        entity_inn: reqInn.trim() || undefined,
        entity_kpp: reqKpp.trim() || undefined,
        account_type: reqType,
        purpose_label: reqPurpose,
      }).unwrap();
      setRequisitesAccount(null);
    } catch (err: unknown) {
      setErrorNotice(apiErrorText(err, 'Не удалось сохранить реквизиты счёта'));
    }
  }

  async function handleCreateAccount(e: FormEvent) {
    e.preventDefault();
    if (!bankName.trim() || !accountNumber.trim()) return;
    try {
      setErrorNotice(null);
      await createAccount({
        bank_name: bankName.trim(),
        bik: bik.trim(),
        account_number: accountNumber.trim(),
        corr_account: corrAccount.trim(),
        account_type: accountType,
        entity_name: entityName.trim(),
        entity_inn: entityInn.trim() || undefined,
        entity_kpp: entityKpp.trim() || undefined,
        current_balance: Number(currentBalance) || 0,
        purpose_label: purposeLabel.trim(),
      }).unwrap();
      resetAccountForm();
      setShowAddAccount(false);
    } catch (err: unknown) {
      setErrorNotice(apiErrorText(err, 'Не удалось сохранить банковский счёт'));
    }
  }

  async function handleCreatePayment(e: FormEvent) {
    e.preventDefault();
    const amt = Number(payAmount);
    if (!payCounterparty.trim() || !amt || amt <= 0) return;
    try {
      setErrorNotice(null);
      await createPayment({
        account_id: payAccountId || undefined,
        payment_date: payDate,
        direction: payDirection,
        amount: amt,
        counterparty_name: payCounterparty.trim(),
        purpose: payPurpose.trim() || 'Операционный платёж',
        category: payCategory || undefined,
        document_number: payDocNo.trim() || undefined,
      }).unwrap();
      setPayAmount('');
      setPayCounterparty('');
      setPayPurpose('');
      setPayCategory('');
      setPayDocNo('');
      setShowAddPayment(false);
    } catch {
      setErrorNotice('Не удалось провести платёж. Проверьте данные и повторите попытку.');
    }
  }

  if (isLoadingAccounts) {
    return <Card>Загрузка банковских счетов и выписок…</Card>;
  }

  if (isErrorAccounts || isErrorDaily) {
    return (
      <Card>
        <ErrorRow>
          <span>Не удалось загрузить данные банковских счетов и платежей.</span>
          <Button
            $size="sm"
            $variant="secondary"
            onClick={() => {
              refetchAccounts();
              refetchDaily();
            }}
          >
            Повторить
          </Button>
        </ErrorRow>
      </Card>
    );
  }

  return (
    <>
      {!editable && <TreasuryViewNote section="Счета и выписки" to="/agents/finance/accounts" />}
      <Section>
        <HeaderRow>
          <div>
            <Title>Банковские счета холдинга и выписки</Title>
            <SummarySubtext>
              Консолидированный остаток:{' '}
              <strong>{summary ? formatMoney(summary.total_current_balance) : '—'}</strong> ·
              Доступная ликвидность:{' '}
              <strong>{summary ? formatMoney(summary.free_operational_cash) : '—'}</strong> ·
              Спецсчета ОБС: <strong>{summary ? formatMoney(summary.special_obs_balance) : '—'}</strong>
            </SummarySubtext>
          </div>
          {can_write && (
            <HeaderActions>
              <WrapButton
                $size="sm"
                disabled={isSyncing}
                onClick={() => handleSync()}
              >
                {isSyncing ? 'Обновление…' : 'Обновить счета'}
              </WrapButton>
              <WrapButton
                $size="sm"
                $variant="secondary"
                onClick={() => setShowUpload((v) => !v)}
              >
                Загрузить выписку
              </WrapButton>
              <WrapButton
                $size="sm"
                $variant="secondary"
                onClick={() => {
                  resetAccountForm();
                  setShowAddAccount((v) => !v);
                }}
              >
                + Добавить счёт
              </WrapButton>
            </HeaderActions>
          )}
        </HeaderRow>

        {syncNotice && (
          <Card>
            <SuccessNotice>{syncNotice}</SuccessNotice>
          </Card>
        )}

        {errorNotice && (
          <Card>
            <ErrorNotice>{errorNotice}</ErrorNotice>
          </Card>
        )}

        {can_write && showUpload && <StatementUploadForm onClose={() => setShowUpload(false)} />}

        {can_write && balanceAccount && (
          <ManualBalanceForm account={balanceAccount} onClose={() => setBalanceAccount(null)} />
        )}

        {can_write && showAddAccount && (
          <AddAccountModal
            bankName={bankName}
            setBankName={setBankName}
            bik={bik}
            setBik={setBik}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            corrAccount={corrAccount}
            setCorrAccount={setCorrAccount}
            entityName={entityName}
            setEntityName={setEntityName}
            entityInn={entityInn}
            setEntityInn={setEntityInn}
            entityKpp={entityKpp}
            setEntityKpp={setEntityKpp}
            accountType={accountType}
            setAccountType={setAccountType}
            currentBalance={currentBalance}
            setCurrentBalance={setCurrentBalance}
            purposeLabel={purposeLabel}
            setPurposeLabel={setPurposeLabel}
            onSubmit={handleCreateAccount}
            onClose={() => {
              resetAccountForm();
              setShowAddAccount(false);
            }}
          />
        )}

        {can_write && requisitesAccount && (
          <AccountRequisitesModal
            accountNumber={requisitesAccount.account_number}
            reqName={reqName}
            setReqName={setReqName}
            reqInn={reqInn}
            setReqInn={setReqInn}
            reqKpp={reqKpp}
            setReqKpp={setReqKpp}
            reqBik={reqBik}
            setReqBik={setReqBik}
            reqCorr={reqCorr}
            setReqCorr={setReqCorr}
            reqType={reqType}
            setReqType={setReqType}
            reqPurpose={reqPurpose}
            setReqPurpose={setReqPurpose}
            isSaving={isSavingRequisites}
            onSubmit={handleSaveRequisites}
            onClose={() => setRequisitesAccount(null)}
          />
        )}

        {groupByCompany(accounts).map(([company, list]) => (
          <Section key={list[0].id}>
            <CompanyTitle>
              <span>{company}</span>
              <span>
                {formatMoney(list.reduce((sum, acc) => sum + acc.current_balance, 0))}
              </span>
            </CompanyTitle>
            <CompanyAccountsGrid>
              {list.map((acc) => (
                <AccountCard
                  key={acc.id}
                  acc={acc}
                  can_write={can_write}
                  onOpenRequisites={openRequisites}
                  onOpenBalance={setBalanceAccount}
                  onSync={handleSync}
                />
              ))}
            </CompanyAccountsGrid>
          </Section>
        ))}
      </Section>

      <BankConnectionsSection canWrite={can_write} onSynced={refreshAfterSync} />

      <DailyPaymentsSection
        dailyData={dailyData}
        accounts={accounts}
        can_write={can_write}
        showAddPayment={showAddPayment}
        setShowAddPayment={setShowAddPayment}
        payAccountId={payAccountId}
        setPayAccountId={setPayAccountId}
        payDate={payDate}
        setPayDate={setPayDate}
        payDirection={payDirection}
        setPayDirection={setPayDirection}
        payAmount={payAmount}
        setPayAmount={setPayAmount}
        payCounterparty={payCounterparty}
        setPayCounterparty={setPayCounterparty}
        payPurpose={payPurpose}
        setPayPurpose={setPayPurpose}
        payCategory={payCategory}
        setPayCategory={setPayCategory}
        payDocNo={payDocNo}
        setPayDocNo={setPayDocNo}
        onCreatePayment={handleCreatePayment}
        filterDate={filterDate}
        setFilterDate={setFilterDate}
        filterAccount={filterAccount}
        setFilterAccount={setFilterAccount}
        filterDirection={filterDirection}
        setFilterDirection={setFilterDirection}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        onResetFilter={() => {
          setFilterDate('');
          setFilterAccount('');
          setFilterDirection('');
          setFilterCategory('');
        }}
      />

      <BalanceHistorySection history={balanceHistory} />
    </>
  );
}
